<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { Document, Download, View } from "@element-plus/icons-vue";
import Notification from "@/utils/plugins/NotificationService";
import SocialFundApplicationService from "@/api/services/socialFundApplication.service";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  reference: {
    type: String,
    required: true,
  },
  document: {
    type: Object,
    default: null,
  },
  initialStatus: {
    type: String,
    default: "validated",
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "confirm"]);
const validationStatus = ref("validated");
const rejectionReason = ref("");
const rejectionError = ref("");

const visible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const fileName = computed(() => props.document?.originalName || props.document?.documentTypeLabel || "documento");
const isReadOnly = computed(() => props.readOnly || props.document?.status !== "pending");

// F5 (fondo_social/v1-20260923/PLAN_FRONTEND.md): visor inline sin descargar.
// El navegador ya trae un visor de PDF nativo — basta con descargar el blob
// con la petición autenticada existente y mostrarlo en un <iframe>/<img> vía
// Object URL; no se puede poner la URL del endpoint directamente en el src
// porque el iframe no manda el header Authorization.
const previewUrl = ref(null);
const previewKind = ref(null); // 'pdf' | 'image' | null
const previewLoading = ref(false);
const previewError = ref("");
const pdfPreviewUrl = computed(() => previewUrl.value ? `${previewUrl.value}#view=FitH` : "");

const revokePreview = () => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = null;
  previewKind.value = null;
  previewError.value = "";
};

watch(() => props.modelValue, (isVisible) => {
  revokePreview();
  if (!isVisible) return;
  validationStatus.value = props.initialStatus === "rejected" ? "rejected" : "validated";
  rejectionReason.value = props.document?.rejectionReason || "";
  rejectionError.value = "";
});

onBeforeUnmount(revokePreview);

const togglePreview = () => {
  if (previewUrl.value) {
    revokePreview();
    return;
  }
  if (!props.document?.downloadUrl) {
    Notification.warning("No se ha podido obtener el archivo del documento.", 3500);
    return;
  }

  previewLoading.value = true;
  previewError.value = "";
  SocialFundApplicationService.downloadDocument(props.document.downloadUrl)
    .then((response) => {
      const contentType = response.headers?.["content-type"] || "";
      if (contentType.includes("pdf")) {
        previewKind.value = "pdf";
      } else if (contentType.startsWith("image/")) {
        previewKind.value = "image";
      } else {
        previewError.value = "Vista previa no disponible para este tipo de archivo. Descárgalo para verlo.";
        return;
      }
      const blob = new Blob([response.data], { type: contentType });
      previewUrl.value = URL.createObjectURL(blob);
    })
    .catch(() => {
      previewError.value = "No se ha podido cargar la vista previa.";
    })
    .finally(() => {
      previewLoading.value = false;
    });
};

const close = () => {
  rejectionError.value = "";
  revokePreview();
  visible.value = false;
};

// FS-S01: downloadUrl es un endpoint autenticado, no una ruta pública — hay
// que pedirlo con el JWT (Api()) y descargar el blob resultante.
const downloadDocument = () => {
  if (!props.document?.downloadUrl) {
    Notification.warning("No se ha podido obtener el archivo del documento.", 3500);
    return;
  }

  SocialFundApplicationService.downloadDocument(props.document.downloadUrl)
    .then((response) => {
      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName.value;
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch(() => {
      Notification.addNotification("Error al descargar el documento.", "error", 6000);
    });
};

const saveValidation = () => {
  if (validationStatus.value === "rejected" && !rejectionReason.value.trim()) {
    rejectionError.value = "Indica el motivo de rechazo del documento.";
    return;
  }

  emit("confirm", {
    document: props.document,
    status: validationStatus.value,
    rejectionReason: validationStatus.value === "rejected" ? rejectionReason.value.trim() : "",
  });
  close();
};
</script>

<template>
  <el-dialog v-model="visible" width="620px" :show-close="false" :lock-scroll="true"
    class="social-fund-document-dialog">
    <section v-if="visible && document" class="social-fund-document-dialog__content">
      <img class="social-fund-document-dialog__close pointer" src="@/assets/images/aspa_blue_small_entrada.png"
        alt="Cerrar" @click="close" />
      <header class="social-fund-document-dialog__header">
        <el-icon>
          <Document />
        </el-icon>
        <h2>{{ isReadOnly ? "Ver documentación" : "Validar documentación" }}</h2>
      </header>
      <p class="social-fund-document-dialog__intro">
        {{ isReadOnly ? "Documento de la postulación:" : "Estás validando un documento de la postulación:" }}
      </p>

      <div class="social-fund-document-dialog__context">
        <span><el-icon>
            <Document />
          </el-icon></span>
        <p><small>Postulación</small><strong>{{ reference }}</strong></p>
        <p><small>Documento</small><strong>{{ document.name }}</strong></p>
      </div>

      <div class="social-fund-document-dialog__attached">
        <label>Documento adjunto</label>
        <div class="social-fund-document-dialog__file">
          <span class="social-fund-document-dialog__file-preview"><el-icon>
              <Document />
            </el-icon></span>
          <div class="social-fund-document-dialog__file-info">
            <strong>{{ fileName }}</strong>
            <small>Subido el {{ document.uploadedAt || "15/04/2024 09:45" }} por {{ document.uploadedBy ||
              "Administración Fondo Social" }}</small>
          </div>
          <button type="button" class="social-fund-document-dialog__download" @click="togglePreview">
            <el-icon>
              <View />
            </el-icon>{{ previewLoading ? "Cargando..." : (previewUrl ? "Ocultar" : "Ver") }}
          </button>
          <button type="button" class="social-fund-document-dialog__download" @click="downloadDocument">
            <el-icon>
              <Download />
            </el-icon>Descargar
          </button>
        </div>
        <p v-if="previewError" class="social-fund-document-dialog__preview-error">{{ previewError }}</p>
        <div v-if="previewUrl" class="social-fund-document-dialog__preview">
          <iframe v-if="previewKind === 'pdf'" :src="pdfPreviewUrl" scrolling="no"
            title="Vista previa del documento" />
          <img v-else-if="previewKind === 'image'" :src="previewUrl" alt="Vista previa del documento" />
        </div>
      </div>

      <template v-if="!isReadOnly">
        <div class="social-fund-document-dialog__decision">
          <label>Estado de validación <b>*</b></label>
          <el-radio-group v-model="validationStatus" class="social-fund-document-dialog__radios">
            <el-radio value="validated"
              class="social-fund-document-dialog__radio social-fund-document-dialog__radio--validated">
              <span><strong>VALIDAR</strong><small>El documento cumple con los requisitos establecidos.</small></span>
            </el-radio>
            <el-radio value="rejected"
              class="social-fund-document-dialog__radio social-fund-document-dialog__radio--rejected">
              <span><strong>RECHAZAR</strong><small>El documento no cumple con los requisitos
                  establecidos.</small></span>
            </el-radio>
          </el-radio-group>
        </div>

        <div v-if="validationStatus === 'rejected'" class="form-item social-fund-document-dialog__reason">
          <label for="rejection-reason">Motivo de rechazo <b>*</b></label>
          <el-input id="rejection-reason" v-model="rejectionReason" type="textarea" :rows="3" maxlength="500"
            show-word-limit placeholder="Describe el motivo de rechazo..." :class="{ 'is-error': rejectionError }"
            @input="rejectionError = ''" />
          <small v-if="rejectionError" class="social-fund-document-dialog__error">{{ rejectionError }}</small>
          <small v-else>Este campo es obligatorio al rechazar un documento.</small>
        </div>
      </template>

      <p v-else-if="document.status === 'rejected' && document.rejectionReason"
        class="social-fund-document-dialog__reason-readonly">
        <strong>Motivo de rechazo:</strong> {{ document.rejectionReason }}
      </p>

      <footer class="social-fund-document-dialog__actions">
        <template v-if="isReadOnly">
          <button type="button" class="btn btn_yellow" @click="close">CERRAR</button>
        </template>
        <template v-else>
          <button type="button" class="btn btn_yellow" @click="close">CANCELAR</button>
          <button type="button" class="btn btn_red" @click="saveValidation">GUARDAR VALIDACIÓN</button>
        </template>
      </footer>
    </section>
  </el-dialog>
</template>

<style lang="scss" scoped>
// Element Plus bloquea body con esta clase. Bloquear también el scroll
// raíz evita que la rueda del modal se propague a la página de fondo.
// Sin reservar gutter ni alterar el ancho del documento o del diálogo.
:global(html:has(body.el-popup-parent--hidden .social-fund-document-dialog)) {
  overflow: hidden;
}

:global(.el-dialog.social-fund-document-dialog) {
  box-sizing: border-box;
  border-radius: 20px;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  padding: 0;
}

:global(.el-dialog.social-fund-document-dialog .el-dialog__header) {
  display: none;
}

:global(.el-dialog.social-fund-document-dialog .el-dialog__body) {
  padding: 0;
}

.social-fund-document-dialog__content {
  color: #263052;
  padding: 22px 24px 16px;
  position: relative;
}

.social-fund-document-dialog__close {
  height: 28px;
  object-fit: contain;
  position: absolute;
  right: 16px;
  top: 16px;
  width: 28px;
}

.social-fund-document-dialog__header {
  align-items: center;
  display: flex;
  gap: 13px;
  padding-right: 36px;
}

.social-fund-document-dialog__header .el-icon {
  color: $bg-blue;
  flex: 0 0 auto;
  font-size: 30px;
}

.social-fund-document-dialog__header h2 {
  color: $bg-blue;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -.05em;
  line-height: 1.1;
  margin: 0;
}

.social-fund-document-dialog__intro {
  font-size: 14px;
  margin: 23px 0 11px;
}

.social-fund-document-dialog__context {
  align-items: center;
  background: #f8f8fc;
  border: 1px solid #dce0e9;
  border-radius: 8px;
  display: grid;
  grid-template-columns: 54px minmax(0, .8fr) minmax(0, 1.4fr);
  padding: 10px 13px;
}

.social-fund-document-dialog__context>span {
  align-items: center;
  background: #e9ebff;
  border-radius: 50%;
  color: $bg-blue;
  display: inline-flex;
  font-size: 22px;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.social-fund-document-dialog__context p {
  border-left: 1px solid #dce0e9;
  font-size: 13px;
  margin: 0;
  padding-left: 17px;
}

.social-fund-document-dialog__context small,
.social-fund-document-dialog__context strong {
  display: block;
}

.social-fund-document-dialog__context small {
  color: #626a81;
  font-size: 11px;
  margin-bottom: 3px;
}

.social-fund-document-dialog__context strong {
  color: #273d9e;
  font-size: 13px;
}

.social-fund-document-dialog__attached {
  margin-top: 18px;
}

.social-fund-document-dialog__attached>label,
.social-fund-document-dialog__decision>label,
.social-fund-document-dialog__reason label {
  color: #283150;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 7px;
}

.social-fund-document-dialog__file {
  align-items: center;
  border: 1px solid #d4d9e5;
  border-radius: 8px;
  display: flex;
  gap: 13px;
  min-height: 94px;
  padding: 12px 14px;
}

.social-fund-document-dialog__file-preview {
  align-items: center;
  background: #f1f2f8;
  border: 1px solid #dce0e9;
  color: $bg-blue;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 30px;
  height: 58px;
  justify-content: center;
  width: 45px;
}

.social-fund-document-dialog__file-info {
  display: grid;
  min-width: 0;
}

.social-fund-document-dialog__file-info strong {
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.social-fund-document-dialog__file-info span {
  color: #4d5570;
  font-size: 11px;
  margin-top: 5px;
}

.social-fund-document-dialog__file-info span i {
  background: #8990a5;
  border-radius: 50%;
  display: inline-block;
  height: 3px;
  margin: 0 7px 2px;
  width: 3px;
}

.social-fund-document-dialog__file-info small {
  color: #7b8398;
  font-size: 10px;
  margin-top: 4px;
}

.social-fund-document-dialog__download {
  align-items: center;
  background: transparent;
  border: 0;
  color: $bg-blue;
  display: inline-flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 800;
  gap: 7px;
  margin-left: auto;
  padding: 6px;
}

.social-fund-document-dialog__download .el-icon {
  font-size: 19px;
}

.social-fund-document-dialog__preview-error {
  color: #d31c5e;
  font-size: 12px;
  margin: 10px 0 0;
}

.social-fund-document-dialog__preview {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  border: 1px solid #d4d9e5;
  border-radius: 8px;
  margin-top: 12px;
  overflow: hidden;
}

.social-fund-document-dialog__preview iframe {
  box-sizing: border-box;
  min-width: 0;
  // El PDF conserva su propio scroll; el frame exterior no debe duplicarlo.
  overflow: hidden;
  max-width: 100%;
  border: 0;
  display: block;
  height: 480px;
  width: 100%;
}

.social-fund-document-dialog__preview img {
  display: block;
  max-height: 480px;
  width: 100%;
  object-fit: contain;
}

.social-fund-document-dialog__decision {
  margin-top: 20px;
}

.social-fund-document-dialog__reason-readonly {
  background: #f8f8fc;
  border: 1px solid #dce0e9;
  border-radius: 8px;
  color: #263052;
  font-size: 13px;
  margin-top: 18px;
  padding: 10px 13px;
}

.social-fund-document-dialog__decision>label b,
.social-fund-document-dialog__reason label b {
  color: #f9156f;
}

.social-fund-document-dialog__radios {
  display: grid;
  gap: 12px;
}

.social-fund-document-dialog__radio {
  align-items: flex-start;
  height: auto;
  margin: 0;
}

.social-fund-document-dialog__radio :deep(.el-radio__input) {
  margin-top: 2px;
}

.social-fund-document-dialog__radio :deep(.el-radio__label) {
  color: #27304f;
  padding-left: 9px;
}

.social-fund-document-dialog__radio strong,
.social-fund-document-dialog__radio small {
  display: block;
}

.social-fund-document-dialog__radio strong {
  font-size: 13px;
}

.social-fund-document-dialog__radio small {
  color: #69718a;
  font-size: 12px;
  margin-top: 3px;
}

.social-fund-document-dialog__radio--validated :deep(.el-radio__input.is-checked .el-radio__inner) {
  background: #2fa354;
  border-color: #2fa354;
}

.social-fund-document-dialog__radio--validated :deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #1f7d40;
}

.social-fund-document-dialog__radio--rejected :deep(.el-radio__input.is-checked .el-radio__inner) {
  background: #f9156f;
  border-color: #f9156f;
}

.social-fund-document-dialog__radio--rejected :deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #c91459;
}

.social-fund-document-dialog__reason {
  margin-top: 19px;
}

.social-fund-document-dialog__reason :deep(.el-textarea__inner) {
  border-radius: 8px;
  min-height: 80px !important;
  padding: 10px 12px;
}


.social-fund-document-dialog__reason .is-error :deep(.el-textarea__inner) {
  box-shadow: 0 0 0 1px #f9156f inset !important;
}

.social-fund-document-dialog__reason>small {
  color: #7a8296;
  display: block;
  font-size: 10px;
  margin-top: 5px;
}

.social-fund-document-dialog__error {
  color: #d31c5e !important;
}

.social-fund-document-dialog__actions {
  border-top: 1px solid #e2e5ed;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin: 18px -24px -16px;
  padding: 15px 24px;
}

.social-fund-document-dialog__actions .btn {
  align-items: center;
  display: inline-flex;
  font-weight: 800;
  justify-content: center;
  margin: 0 !important;
  min-height: 40px;
  min-width: 170px;
}

.social-fund-document-dialog__actions .btn_yellow {
  color: $bg-blue !important;
}

.social-fund-document-dialog__actions .btn_red {
  color: #fff !important;
}

@media (max-width: 767px) {
  .social-fund-document-dialog__content {
    padding: 18px 16px 14px;
  }

  .social-fund-document-dialog__header {
    gap: 10px;
  }

  .social-fund-document-dialog__header .el-icon {
    font-size: 26px;
  }

  .social-fund-document-dialog__header h2 {
    font-size: 25px;
  }

  .social-fund-document-dialog__context {
    gap: 9px;
    grid-template-columns: 48px 1fr;
  }

  .social-fund-document-dialog__context p:last-child {
    grid-column: 2;
  }

  .social-fund-document-dialog__file {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .social-fund-document-dialog__file-info {
    flex: 1;
  }

  .social-fund-document-dialog__download {
    margin-left: 58px;
  }

  .social-fund-document-dialog__actions {
    flex-direction: column-reverse;
    margin: 18px -16px -14px;
    padding: 14px 16px;
  }

  .social-fund-document-dialog__actions .btn {
    width: 100%;
  }
}
</style>
